import React, { useRef, useEffect, useState, useContext, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../api";
import { ChevronDown, ChevronRight, History, CheckCircle, XCircle, Clock, Trophy, Sparkles } from "lucide-react";
import { UserContext } from "../UserContext";
import { ThemeContext } from "../ThemeContext";
import Dropdown from "../components/Dropdown.js";
import ImageViewer from "../components/ImageViewer";
import { motion, AnimatePresence } from "framer-motion";

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl p-3 sm:p-4 md:p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
        overflow: className.includes('mb-6') && delay === 0.2 ? 'visible' : undefined,
      }}
    >
      {/* Animated gradient border on hover */}
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

const SmoothExpand = ({ isOpen, children }) => {
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [isOpen, children]);

  return (
    <motion.div
      initial={false}
      animate={{
        maxHeight: isOpen ? maxHeight : "0px",
      }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      style={{
        overflow: "hidden",
      }}
    >
      <div ref={contentRef}>
        {children}
      </div>
    </motion.div>
  );
};

const AnswerHistoryPage = () => {
  const [answerHistory, setAnswerHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('All Subjects');
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [allSubjects, setAllSubjects] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandRow, setExpandRow] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState({});
  const itemsPerPage = 10;
  const { user } = useContext(UserContext);
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isPremium = (user && user.is_premium === true) || (JSON.parse(localStorage.getItem('sat_user'))?.is_premium === true);
  const userData = user || JSON.parse(localStorage.getItem('sat_user'));
  const trialUsed = userData ? (userData.trial_used === true) : false;

  useEffect(() => {
    if (user && user.user_id) {
      setIsLoading(true);
      // Build query params
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', 10);
      if (activeTab !== 'all') {
        params.append('question_status', activeTab);
      }
      if (selectedSubject !== 'All Subjects') {
        params.append('content_name', selectedSubject);
      }
      
       api.get(`/api/user/answer_history/?${params.toString()}`)
         .then((response) => {
           setAnswerHistory(response.data.answer_history || []);
           setTotalPages(response.data.total_pages || 1);
           setTotalCount(response.data.count || 0);
           setAllSubjects(response.data.available_subjects || []);
         })
         .catch((error) => {
           console.error("There was an error fetching the answer history!", error);
           setAnswerHistory([]);
         })
         .finally(() => {
           setIsLoading(false);
         });
    } else {
      setIsLoading(false);
    }
  }, [user, currentPage, activeTab, selectedSubject]);

  useEffect(() => {
    const expandedItem = answerHistory.find(q => q.score_id === expanded);
    if (expanded && expandedItem && !expandedDetails[expanded]) {
      api.get(`/api/solo/get_question/${expandedItem.question_id}`)
        .then(res => {
          console.log("Fetched question for", expanded, res.data);
          setExpandedDetails(prev => ({ ...prev, [expanded]: res.data }));
        })
        .catch(err => {
          console.error('Failed to fetch expanded question:', err);
        });
    }
  }, [expanded]);


  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
    setCurrentPage(1);
  };

  const subjects = useMemo(() => {
    return ['All Subjects', ...allSubjects];
  }, [allSubjects]);


  const handleRowClick = (entry) => {
    if (isMobile) {
      // Mobile view → redirect
      navigate(`/answer-history/${entry.score_id}`, { state: { answerDetails: entry } });
      window.scrollTo(0, 0);
    } else {
      // Desktop view → expand logic
      if (expanded === entry.score_id) {
        
        setExpandedRows({});
        setExpanded(null);
        setTimeout(() => {
          setExpandRow(null);
        }, 300);
      } else {
        console.log("Collapsing row", entry.score_id);
        setExpandedRows({ [entry.score_id]: true });
        setExpanded(entry.score_id);
        setExpandRow(entry.score_id);
      }
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center text-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24 pl-0 md:pl-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-4"
          >
            <History className="w-16 h-16 text-cyan-500" />
          </motion.div>
          <h1 className="text-2xl font-bold text-gray-600 dark:text-gray-300 font-zonaProRegular">Loading History...</h1>
        </motion.div>
      </div>
    );
  }

if (!isPremium) {
  return (
      <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl p-8 text-center shadow-2xl border border-white/20 dark:border-gray-700/50 max-w-md"
            >
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="inline-block mb-4"
              >
                <Trophy className="w-12 h-12 text-yellow-400" />
              </motion.div>
              <h3 className="text-2xl font-zonaProUltra text-gray-800 dark:text-gray-200 mb-2">Subscription Required</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">
                {trialUsed 
                  ? 'Subscribe to view your detailed Answer History.'
                  : 'Start your 7-day free trial to access your detailed Answer History.'}
              </p>
              <motion.button
            onClick={() => navigate('/subscription')}
                className="py-3 px-8 bg-[#34c759] text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
          >
            {trialUsed ? 'Subscribe' : 'Start Free Trial'}
              </motion.button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
  );
}
  const hasActiveFilters = () => {
    return activeTab !== 'all' || selectedSubject !== 'All Subjects';
   };

  const getResultHeader = (status) => {
    switch (status) {
      case 'correct': return "CORRECT!";
      case 'incorrect': return "INCORRECT";
      case 'unanswered': return "UNANSWERED";
      default: return "Result";
    }
  }

  const getResultBorderColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-400';
      case 'incorrect': return 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-400';
      case 'unanswered': return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-400';
      default: return 'border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-600';
    }
  }

  const getResultTextColor = (status) => {
    switch (status) {
      case 'correct': return 'text-green-700 dark:text-green-300';
      case 'incorrect': return 'text-red-700 dark:text-red-300';
      case 'unanswered': return 'text-yellow-700 dark:text-yellow-300';
      default: return 'text-gray-700 dark:text-gray-300';
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'incorrect': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'unanswered': return <Clock className="w-5 h-5 text-yellow-500" />;
      default: return null;
    }
  }

  // Helper function to get answer option keys
  const getAnswerOptionKeys = (question) => {
    const answerOptionKeys = ['A', 'B', 'C', 'D'];
    return answerOptionKeys.map(key => {
      const value = question['option_' + key.toLowerCase()];
      return value ? { key, value } : null;
    }).filter(opt => opt !== null);
  };


  const renderTextWithPi = (text) => {
    if (!text.includes('π')) return text;

    return text.split('π').map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < text.split('π').length - 1 && (
          <span className="font-sans">π</span>
        )}
      </React.Fragment>
    ));
  };

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Main Content */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 w-full max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Answer History
          </h1>
          <motion.p
            className="text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Review your past questions and track your progress
          </motion.p>
        </motion.div>

        {answerHistory && answerHistory.length > 0 ? (
          <>
            <div className="mb-6" style={{ position: 'relative', zIndex: 50, overflow: 'visible' }}>
              <Card className="mb-6" delay={0.2} style={{ position: 'relative', zIndex: 50, overflow: 'visible' }}>
                <div className="flex flex-col gap-4" style={{ overflow: 'visible' }}>
                  <div className="flex flex-row items-center gap-3" style={{ position: 'relative', zIndex: 10000, overflow: 'visible' }}>
                    <div className="sm:hidden flex-1" style={{ position: 'relative', zIndex: 10000 }}>
                      <Dropdown
                        options={['All', 'Correct', 'Incorrect', 'Unanswered']}
                        selectedOption={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                        onOptionChange={(option) => handleTabChange(option.toLowerCase())}
                        scrollable={false}
                      />
                    </div>
                    
                    <div className="hidden sm:flex flex-wrap gap-2">
                      {['all', 'correct', 'incorrect', 'unanswered'].map((tab) => (
                        <motion.button
                          key={tab}
                          onClick={() => handleTabChange(tab)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-4 py-2 rounded-xl font-zonaProRegular transition-all ${
                            activeTab === tab
                              ? 'text-white shadow-lg'
                              : 'bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/70'
                          }`}
                          style={activeTab === tab ? { backgroundColor: '#06bae4' } : {}}
                        >
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </motion.button>
                      ))}
                    </div>

                    <div className="flex-1 sm:flex-none sm:ml-auto" style={{ position: 'relative', zIndex: 10000 }}>
                      <Dropdown
                        options={subjects}
                        selectedOption={selectedSubject}
                        onOptionChange={handleSubjectChange}
                        scrollable={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <motion.button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className={`p-2 rounded-lg transition-all ${
                        currentPage === 1
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                          : 'bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/70'
                      }`}
                    >
                      &larr;
                    </motion.button>
                    <span className="font-zonaProRegular text-gray-700 dark:text-gray-300 min-w-[100px] text-center">
                      Page {currentPage} of {totalPages}
                    </span>
                    <motion.button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className={`p-2 rounded-lg transition-all ${
                        currentPage === totalPages || totalPages === 0
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                          : 'bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/70'
                      }`}
                    >
                      &rarr;
                    </motion.button>
                  </div>
                </div>
              </Card>
            </div>

        {/* History Entries */}
        <div className="space-y-4" style={{ position: 'relative', zIndex: 1, isolation: 'isolate' }}>
          {/* Column Headers */}
          <Card className="mb-2 hidden md:block" delay={0.25}>
            <div className="grid grid-cols-12 gap-2 md:gap-4 items-center border-b border-gray-200 dark:border-gray-700 pb-4">
              <div className="col-span-1 text-center font-zonaProBlack text-base md:text-lg" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>#</div>
              <div className="col-span-1"></div>
              <div className="col-span-2 md:col-span-2 text-left font-zonaProBlack text-base md:text-lg" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Status</div>
              <div className="col-span-5 md:col-span-2 text-left font-zonaProBlack text-base md:text-lg" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Subject</div>
              <div className="hidden md:flex col-span-2 text-left font-zonaProBlack text-lg" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Points</div>
              <div className="hidden md:flex col-span-2 text-left font-zonaProBlack text-lg" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Time</div>
              <div className="hidden md:flex col-span-2 text-left font-zonaProBlack text-lg" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Date</div>
            </div>
          </Card>
          
          <AnimatePresence>
            {answerHistory.map((entry, idx) => (
              <motion.div
                key={entry.score_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: idx * 0.05, duration: 0.4 }}
              >
                <Card className="overflow-hidden" delay={0.3 + idx * 0.05}>
                  <motion.div
                  onClick={() => handleRowClick(entry)}
                    className="cursor-pointer"
                    whileHover={{ scale: 1.01 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Mobile Layout */}
                    <div className="md:hidden space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-zonaProRegular text-gray-500 dark:text-gray-400">#{(currentPage - 1) * itemsPerPage + idx + 1}</span>
                          {getStatusIcon(entry.question_status)}
                          <span className={`font-zonaProRegular font-semibold text-sm ${
                            entry.question_status === 'correct'
                              ? 'text-green-500 dark:text-green-400'
                              : entry.question_status === 'incorrect'
                                ? 'text-red-500 dark:text-red-400'
                                : entry.question_status === 'unanswered'
                                  ? 'text-yellow-500 dark:text-yellow-400'
                                  : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {entry.question_status}
                          </span>
                        </div>
                        <motion.div
                          animate={{ rotate: expanded === entry.score_id ? 90 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {expanded === entry.score_id ? <ChevronDown size={18} className="text-cyan-500" /> : <ChevronRight size={18} className="text-gray-400" />}
                        </motion.div>
                      </div>
                      <div className="font-zonaProRegular text-base font-semibold text-gray-800 dark:text-gray-200">
                        {entry.content_name === 'Sentence Completion' ? 'Vocabulary In Context' : entry.content_name}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-zonaProRegular font-bold text-lime-500">
                          {entry.points > 0 ? `+${entry.points}` : entry.points} pts
                        </span>
                        <span className="font-zonaProRegular text-gray-600 dark:text-gray-400">
                          {entry.time_spent}s
                        </span>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-2 md:gap-4 items-center">
                      <div className="col-span-1 text-center font-zonaProRegular text-base md:text-lg text-gray-700 dark:text-gray-300">
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <motion.div
                          animate={{ rotate: expanded === entry.score_id ? 90 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {expanded === entry.score_id ? <ChevronDown size={20} className="text-cyan-500" /> : <ChevronRight size={20} className="text-gray-400" />}
                        </motion.div>
                      </div>
                      <div className="col-span-2 md:col-span-2 flex items-center gap-2">
                        {getStatusIcon(entry.question_status)}
                        <span className={`font-zonaProRegular font-semibold text-base md:text-lg ${
                          entry.question_status === 'correct'
                            ? 'text-green-500 dark:text-green-400'
                            : entry.question_status === 'incorrect'
                              ? 'text-red-500 dark:text-red-400'
                              : entry.question_status === 'unanswered'
                                ? 'text-yellow-500 dark:text-yellow-400'
                                : 'text-gray-500 dark:text-gray-400'
                        }`}>
                    {entry.question_status}
                        </span>
                      </div>
                      <div className="col-span-5 md:col-span-2 text-left font-zonaProRegular text-gray-800 dark:text-gray-200 truncate text-base md:text-lg">
                        {entry.content_name === 'Sentence Completion' ? 'Vocabulary In Context' : entry.content_name}
                      </div>
                      <motion.div
                        className="hidden md:flex col-span-2 text-left font-zonaProRegular font-bold text-lg text-lime-500"
                        animate={{
                          scale: [1, 1.05, 1],
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          delay: idx * 0.2,
                        }}
                      >
                        {entry.points > 0 ? `+${entry.points}` : entry.points}
                      </motion.div>
                      <div className="hidden md:flex col-span-2 text-left font-zonaProRegular text-gray-600 dark:text-gray-400 text-lg">
                        {entry.time_spent}s
                      </div>
                      <div className="hidden md:flex col-span-2 text-left font-zonaProRegular text-gray-600 dark:text-gray-400 text-base">
                        {formatDate(entry.created_date)}
                      </div>
                    </div>
                  </motion.div>
                  
                  {!isMobile && expandRow === entry.score_id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                    <SmoothExpand isOpen={expanded === entry.score_id}>
                        <div className="p-6 bg-gray-50/50 dark:bg-gray-900/50">
                          {/* Mobile Details */}
                          <div className="md:hidden space-y-3 mb-6 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl">
                            <h2 className="text-lg font-zonaProUltra text-gray-800 dark:text-gray-200 mb-3">Game Details</h2>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <span className="font-semibold dark:text-lime-400" style={{ color: '#34c759' }}>Points:</span>
                                <span className="ml-2 text-gray-700 dark:text-gray-300">{entry.points}</span>
                            </div>
                              <div>
                                <span className="font-semibold dark:text-lime-400" style={{ color: '#34c759' }}>Time:</span>
                                <span className="ml-2 text-gray-700 dark:text-gray-300">{entry.time_spent}s</span>
                            </div>
                              <div>
                                <span className="font-semibold dark:text-lime-400" style={{ color: '#34c759' }}>Date:</span>
                                <span className="ml-2 text-gray-700 dark:text-gray-300 text-sm">{formatDate(entry.created_date)}</span>
                            </div>
                            </div>
                        </div>
                        
                        {!expandedDetails[entry.score_id] ? (
                            <div className="flex items-center justify-center py-8">
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="inline-block"
                              >
                                <Sparkles className="w-8 h-8 text-cyan-500" />
                              </motion.div>
                              <span className="ml-3 text-gray-500 dark:text-gray-400 italic font-zonaProRegular">Loading question details...</span>
                            </div>
                        ) : (
                          <>
                            <div className="flex flex-col md:flex-row md:gap-8 font-zonaProRegular"> 
                              {/* Left Side: Question + Feedback */}
                              <div className="md:w-3/5 lg:w-1/2 space-y-5">
                                  <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-200 font-zonaProUltra mb-1">Question:</h1>
                                {entry.image_name && (
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.9 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      transition={{ delay: 0.2 }}
                                      className="my-4 rounded-lg overflow-hidden shadow-lg max-h-60 flex justify-center items-center bg-gray-100 dark:bg-gray-800"
                                    >
                                    <ImageViewer fileName={entry.image_name} alt="Question image" className="max-h-full max-w-full object-contain" />
                                    </motion.div>
                                )}
                                  <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 text-left leading-relaxed whitespace-pre-line">
                                  {expandedDetails[entry.score_id]
                                    ? renderTextWithPi(expandedDetails[entry.score_id].question_choice || entry.question_choice)
                                    : null}
                                </p>
                                  <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className={`mt-4 p-4 rounded-lg border-l-4 ${getResultBorderColor(entry.question_status)}`}
                                  >
                                  <h3 className={`text-lg font-semibold mb-2 font-zonaProUltra ${getResultTextColor(entry.question_status)}`}>
                                    {getResultHeader(entry.question_status)}
                                  </h3>
                                  {expandedDetails[entry.score_id] && (
                                    <div className={`mb-2 ${getResultTextColor(entry.question_status)}`}>
                                      {expandedDetails[entry.score_id].feedback}
                                    </div>
                                  )}
                                  {entry.question_status !== 'unanswered' && entry.points !== undefined && (
                                    <div className={`text-base font-semibold ${getResultTextColor(entry.question_status)}`}>
                                      Points: <span className="font-bold">{entry.points > 0 ? `+${entry.points}` : entry.points}</span>
                                    </div>
                                  )}
                                  </motion.div>
                              </div>

                              {/* Right Side: Answer Options */}
                                <motion.div
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.4 }}
                                  className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6 mt-6 md:mt-0"
                                >
                                  <h2 className="text-lg sm:text-xl font-zonaProUltra text-gray-800 dark:text-gray-200 mb-4">
                                  Results:
                                </h2>
                                {expandedDetails[entry.score_id]?.question_type === 'Grid-in' ? (() => {
                                  const userAnswerRaw = entry.option_selected;
                                  const correctAnswerRaw = expandedDetails[entry.score_id]?.answer;

                                  const userAnswer = String(userAnswerRaw ?? "").trim();
                                  const correctAnswer = String(correctAnswerRaw ?? "").trim();

                                  const isUnanswered = userAnswer === "" || userAnswer === null;
                                  const isCorrect = !isUnanswered && parseFloat(userAnswer) === parseFloat(correctAnswer);
                                  const status = isUnanswered ? "unanswered" : (isCorrect ? "correct" : "incorrect");

                                  return (
                                    <div className="space-y-3">
                                      {status === 'correct' && (
                                          <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300"
                                          >
                                          <span className="font-bold mr-2">Your Answer:</span>
                                          {renderTextWithPi(userAnswer || '—')}
                                          </motion.div>
                                      )}

                                      {status === 'incorrect' && (
                                        <>
                                            <motion.div
                                              initial={{ opacity: 0, scale: 0.9 }}
                                              animate={{ opacity: 1, scale: 1 }}
                                              className="w-full text-left p-3 rounded-lg border-2 font-medium bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-400 text-red-700 dark:text-red-300"
                                            >
                                            <span className="font-bold mr-2">Your Answer:</span>
                                            {renderTextWithPi(userAnswer || '—')}
                                            </motion.div>
                                            <motion.div
                                              initial={{ opacity: 0, scale: 0.9 }}
                                              animate={{ opacity: 1, scale: 1 }}
                                              transition={{ delay: 0.1 }}
                                              className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300"
                                            >
                                            <span className="font-bold mr-2">Correct Answer:</span>
                                            {renderTextWithPi(correctAnswer || '—')}
                                            </motion.div>
                                        </>
                                      )}

                                      {status === 'unanswered' && (
                                          <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300"
                                          >
                                          <span className="font-bold mr-2">Correct Answer:</span>
                                          {renderTextWithPi(correctAnswer || '—')}
                                          </motion.div>
                                      )}
                                    </div>
                                  );
                                })() : (
                                  (() => {
                                    const userAnswerKey = entry.option_selected?.toUpperCase();
                                    const correctAnswerText = expandedDetails[entry.score_id]?.answer;
                                    return (
                                      <div className="space-y-3">
                                          {getAnswerOptionKeys(expandedDetails[entry.score_id] || {}).map((opt, optIdx) => {
                                          const isCorrectAnswer = opt.value === correctAnswerText;
                                          const isUserAnswer = userAnswerKey === opt.key;

                                            let classes = "w-full text-left p-3 rounded-lg border-2 font-medium transition-all";

                                          if (isCorrectAnswer) {
                                              classes += ' bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300';
                                          }
                                          if (isUserAnswer && !isCorrectAnswer) {
                                              classes += ' bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-500 text-red-700 dark:text-red-300';
                                          }
                                          if (!isUserAnswer && !isCorrectAnswer) {
                                              classes += ' bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 opacity-75';
                                          }

                                          return (
                                              <motion.div
                                                key={opt.key}
                                                initial={{ opacity: 0, x: 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.5 + optIdx * 0.1 }}
                                                className={classes}
                                              >
                                              <span className="font-bold mr-2">{opt.key})</span>{renderTextWithPi(opt.value)}
                                              </motion.div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()
                                )}
                                </motion.div>
                            </div>
                          </>
                        )}
                      </div>
                    </SmoothExpand>
                    </motion.div>
                  )}
                </Card>
              </motion.div>
            ))}
           </AnimatePresence>
            </div>
          </>
        ) : (
          <>
            {hasActiveFilters() ? (
              <>
                <div className="mb-6" style={{ position: 'relative', zIndex: 50, overflow: 'visible' }}>
                  <Card className="mb-6" delay={0.2} style={{ position: 'relative', zIndex: 50, overflow: 'visible' }}>
                    <div className="flex flex-col gap-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-row items-center gap-3" style={{ position: 'relative', zIndex: 10000, overflow: 'visible' }}>
                        <div className="sm:hidden flex-1" style={{ position: 'relative', zIndex: 10000 }}>
                          <Dropdown
                            options={['All', 'Correct', 'Incorrect', 'Unanswered']}
                            selectedOption={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                            onOptionChange={(option) => handleTabChange(option.toLowerCase())}
                            scrollable={false}
                          />
                        </div>
                        
                        <div className="hidden sm:flex flex-wrap gap-2">
                          {['all', 'correct', 'incorrect', 'unanswered'].map((tab) => (
                            <motion.button
                              key={tab}
                              onClick={() => handleTabChange(tab)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className={`px-4 py-2 rounded-xl font-zonaProRegular transition-all ${
                                activeTab === tab
                                  ? 'text-white shadow-lg'
                                  : 'bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/70'
                              }`}
                              style={activeTab === tab ? { backgroundColor: '#06bae4' } : {}}
                            >
                              {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </motion.button>
                          ))}
                        </div>

                        <div className="flex-1 sm:flex-none sm:ml-auto" style={{ position: 'relative', zIndex: 10000 }}>
                          <Dropdown
                            options={subjects}
                            selectedOption={selectedSubject}
                            onOptionChange={handleSubjectChange}
                            scrollable={true}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <motion.button
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className={`p-2 rounded-lg transition-all ${
                            currentPage === 1
                              ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                              : 'bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/70'
                          }`}
                        >
                          &larr;
                        </motion.button>
                        <span className="font-zonaProRegular text-gray-700 dark:text-gray-300 min-w-[100px] text-center">
                          Page {currentPage} of {totalPages}
                        </span>
                        <motion.button
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages || totalPages === 0}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className={`p-2 rounded-lg transition-all ${
                            currentPage === totalPages || totalPages === 0
                              ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                              : 'bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/70'
                          }`}
                        >
                          &rarr;
                        </motion.button>
                      </div>
                    </div>
                  </Card>
                </div>

                <div className="flex flex-col justify-center items-center text-center py-12">
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="inline-block mb-6"
                    >
                      <History className="w-20 h-20 text-cyan-500" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2 font-zonaProUltra">No Results Match Your Filters</h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400 font-zonaProRegular">
                      Try adjusting your filters to see more questions.
                    </p>
                  </motion.div>
                </div>
              </>
            ) : (
              <div className="flex flex-col justify-center items-center text-center py-12">
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="inline-block mb-6"
                  >
                    <History className="w-20 h-20 text-cyan-500" />
                  </motion.div>
                  <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-4 font-zonaProUltra">No Questions Attempted Yet!</h1>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 font-zonaProRegular">
                    It looks like you haven't played any game. Start playing to see your answer history.
                  </p>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/options"
                      className="inline-block py-3 px-8 text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg transition-all"
                      style={{ background: 'linear-gradient(to right, #34c759, #34c759)', boxShadow: '0 10px 40px rgba(52, 199, 89, 0.5)' }}
                    >
                      Play Now
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    );
  };

export default AnswerHistoryPage;

